import { Capacitor } from "@capacitor/core";

/**
 * Retorna a base URL para chamadas de API.
 * - Web: vazio (URLs relativas funcionam)
 * - Mobile/Capacitor: URL absoluta do servidor
 */
export function getApiBaseUrl(): string {
  if (Capacitor.isNativePlatform()) {
    return import.meta.env.VITE_API_BASE_URL || "https://rocketanks.nagentes.com";
  }
  return ""; // Web: URL relativa
}

/**
 * Retorna a base URL para WebSocket (Colyseus).
 * - Web: protocolo + host atual
 * - Mobile: URL do servidor com ws:// ou wss://
 */
export function getWsBaseUrl(): string {
  if (Capacitor.isNativePlatform()) {
    return import.meta.env.VITE_WS_BASE_URL || "wss://rocketanks.nagentes.com";
  }
  // Web: comportamento atual
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}`;
}

/**
 * Plataforma do cliente, como o servidor a enxerga.
 *
 * Existe porque o header `Origin` do app nativo é `https://localhost` — um
 * nome que só resolve dentro do aparelho. Endpoints que precisam devolver o
 * jogador ao lugar certo depois de sair do app (checkout do Stripe, portal de
 * assinatura) usam este campo para escolher entre voltar ao site ou ao deep
 * link do app.
 */
export function getClientPlatform(): "android" | "ios" | "web" {
  if (!Capacitor.isNativePlatform()) return "web";
  return Capacitor.getPlatform() === "ios" ? "ios" : "android";
}

/**
 * Helper para construir URL de API.
 * Uso: apiUrl("/api/progress") -> "https://rocketanks.nagentes.com/api/progress" (mobile)
 *                                -> "/api/progress" (web)
 */
export function apiUrl(path: string): string {
  return `${getApiBaseUrl()}${path}`;
}

/**
 * [AUTH] Provedor do token de acesso, registrado pelo useAuth na carga do
 * módulo (indireção para evitar import circular apiBase ↔ useAuth).
 * No app nativo os cookies cross-site não chegam de forma confiável, então
 * TODA chamada de API precisa do Bearer — antes só ~15 de 217 call sites
 * anexavam o header manualmente e o resto falhava autenticação no Android.
 */
let authTokenProvider: (() => string | null) | null = null;

export function setAuthTokenProvider(provider: () => string | null): void {
  authTokenProvider = provider;
}

/**
 * Wrapper do fetch com base URL automatica.
 * Substitui fetch("/api/...") por apiFetch("/api/...").
 * Anexa Authorization automaticamente quando um provedor de token está
 * registrado e o chamador ainda não definiu o header.
 */
export async function apiFetch(
  path: string,
  options?: RequestInit
): Promise<Response> {
  const headers = new Headers(options?.headers);
  if (!headers.has("Authorization") && authTokenProvider) {
    const token = authTokenProvider();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(apiUrl(path), {
    ...options,
    headers,
    credentials: "include",
  });
}

/**
 * Abre URL externa no browser do sistema.
 * Capacitor: usa @capacitor/browser (Custom Tabs no Android).
 * Web: usa window.open().
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
  } else {
    window.open(url, "_blank");
  }
}

/**
 * Redireciona para URL externa no mesmo tab (para checkout do Stripe).
 * Nunca é bloqueado por popup blockers.
 * Capacitor: usa @capacitor/browser.
 * Web: usa window.location.href (redirecionamento direto).
 */
export async function redirectToExternalUrl(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
  } else {
    window.location.href = url;
  }
}

/**
 * Fecha a aba in-app (Custom Tab) aberta por openExternalUrl/redirectToExternalUrl.
 * No web nao ha o que fechar — no-op.
 */
export async function closeInAppBrowser(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch {
    // A aba ja pode ter sido fechada pelo usuario
  }
}

/**
 * Copia texto para a area de transferencia.
 * Capacitor: usa @capacitor/clipboard.
 * Web: usa navigator.clipboard.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { Clipboard } = await import("@capacitor/clipboard");
      await Clipboard.write({ string: text });
      return true;
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Compartilha conteudo via Share nativo.
 * Capacitor: usa @capacitor/share.
 * Web: usa navigator.share (se disponivel).
 */
export async function nativeShare(opts: {
  title?: string;
  text?: string;
  url?: string;
}): Promise<boolean> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { Share } = await import("@capacitor/share");
      await Share.share(opts);
      return true;
    }
    if (navigator.share) {
      await navigator.share(opts);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}
